import React, { useState, useEffect, memo, useMemo, useReducer } from "react";
import {
  Paper,
  Grid,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
} from "@material-ui/core";
import { makeStyles, createStyles, Theme } from "@material-ui/core/styles";

import { RowNoData } from "components";
import {
  Tooltip,
  Button,
  InputForm,
  Autocomplete,
  ButtonCancelIcon,
} from "components/shared";
import { Icons } from "components/Icons";
import { useSet, useTable, useInstallation } from "hooks";
import { IComplements, IAddressee } from "config/interfaces";
import { TableTitle } from "./styles";

interface ComplementsState {
  site: string;
  group: string;
  set: string;
  isAccessible: boolean;
  loading?: boolean;
}

const initialState: ComplementsState = {
  site: "",
  group: "DESTINATÁRIO",
  set: "",
  isAccessible: false,
  loading: false,
};

type ComplementsActions = {
  type: string;
  payload?: ComplementsState;
};

function complementsReducer(
  state: ComplementsState,
  action: ComplementsActions
) {
  switch (action.type) {
    case "SETVALUES": {
      return {
        ...state,
        ...action.payload,
      };
    }
    case "RESET": {
      return { ...initialState };
    }
    default: {
      return state;
    }
  }
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    toolbar: {
      paddingBottom: theme.spacing(2),
      borderRadius: "4px",
      background: "var(--backgroundContent)",
      width: "100%",
      "& .MuiToolbar-regular": {
        padding: 0,
      },
    },
    table: {
      marginTop: "16px",
      borderRadius: "4px",
      background: "var(--backgroundContent)",
      width: "100%",
    },
    margin: {
      margin: theme.spacing(1),
    },
    withoutLabel: {
      marginTop: theme.spacing(3),
    },
    textField: {
      maxWidth: "calc(100% - 12px)",
    },
    input: {
      "& .MuiInputBase-input": {
        boxShadow: "none",
        fontSize: "1.2rem",
        fontWeight: "500",
        textTransform: "uppercase",
      },
    },
    checkbox: {
      "&.MuiCheckbox-colorSecondary.Mui-checked": {
        color: "var(--orange)",
      },
      "& .MuiSvgIcon-root": {
        width: "1.4rem",
      },
    },
  })
);

interface HeadCell {
  disableSorting?: boolean;
  id: keyof IComplements.Table;
  label: string;
}

// TABLE HEAD
const headCells: HeadCell[] = [
  { id: "IDINSTALL", label: "INSTALAÇÃO" },
  { id: "DSMCM", label: "SET" },
  { id: "DSGRUPOUSUARIO", label: "GRUPO DO USUÁRIO" },
];

interface TableProps {
  title?: string;
  records: IAddressee.UserGroup[];
  onAddComplement: (data: any) => void;
  onUpdateComplement: (data: any) => void;
  onDeleteComplement: (IDGRUPOUSUARIO: number) => void;
}

function TableComplements({
  title,
  records,
  onAddComplement,
  onUpdateComplement,
  onDeleteComplement,
}: TableProps) {
  const { loading, installationsShort } = useInstallation();
  const {
    loading: loadingSets,
    setsShort,
    getComplementsBySet,
    complements: complementsOfSet,
    getAllSetsShort,
    getUserGroupsBySet,
    userGroups,
  } = useSet();

  const [rows, setRows] = useState([] as IAddressee.UserGroup[]);
  const [selectedRows] = useState([] as Array<string>);
  const [complements, setComplements] = useState([] as IAddressee.EndeCompl[]);
  const [filterFn] = useState({
    fn: (items: any) => {
      return items;
    },
  });
  const [editing, setEditing] = useState(false);
  const [state, dispatch] = useReducer(complementsReducer, initialState);

  const installationsSelectOptionsAutocomplete = useMemo(() => {
    const options = installationsShort.map(
      (installation) => installation.IDSITE
    );
    return options;
  }, [installationsShort]);

  const setSelectOptionsAutocomplete = useMemo(() => {
    const options = setsShort.map((set) => set.DSMCM);

    return options;
  }, [setsShort]);

  const userGroupsOptionsAutocomplete = useMemo(() => {
    const options = userGroups.map((group) => group.DSGRUPOUSUARIO);

    return options;
  }, [userGroups]);

  useEffect(() => {
    (async () => {
      if (state?.site?.length > 0) {
        await getAllSetsShort({ IDSITE: state.site, withLoading: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.site]);

  useEffect(() => {
    (async () => {
      if (state?.set) {
        await getComplementsBySet(state.site, state.set);
        await getUserGroupsBySet(state.site, state.set);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.set]);

  useEffect(() => {
    if (!editing && !(complements.length > 0) && complementsOfSet.length > 0) {
      const newComplements: IAddressee.EndeCompl[] = complementsOfSet.map(
        (complement, index) => {
          return {
            VLCOMPLEMENTO: "",
            IDTIPOCOMPLEMENTO: complement.IDTIPOCOMPLEMENTO,
          };
        }
      );

      setComplements(newComplements);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [complementsOfSet, editing]);

  const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } =
    useTable({
      records,
      headCells,
      filterFn,
      config: { checkbox: false, actions: true },
    });

  useEffect(() => {
    setRows(records);
  }, [records]);

  const classes = useStyles();

  const handleChangeAutoComplete = (name: string, value: string | null) => {
    const newInstallation = { ...state, [name]: value };
    dispatch({ type: "SETVALUES", payload: newInstallation });
  };

  const handleChangeComplements = (
    e: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    const { value } = e.target;
    let newComplements = [...complements];
    newComplements[index].VLCOMPLEMENTO = value.toUpperCase();
    setComplements(newComplements);
  };

  const clearState = () => {
    setComplements([] as IAddressee.EndeCompl[]);
    dispatch({ type: "RESET" });
    setEditing(false);
  };

  // COMPLEMENTS ACTIONS
  // ---------------------------------------------------------------------
  const handleSubmitComplement = () => {
    const IDMCM = setsShort.find((set) => set.DSMCM === state.set)?.IDMCM;
    const IDGRUPOUSUARIO = userGroups.find(
      (group) => group.DSGRUPOUSUARIO === state.group
    )?.IDGRUPOUSUARIO;

    const newComplement = {
      toSubmit: {
        IDGRUPOUSUARIO,
        FLUSERADM: state.group === "DESTINATÁRIO" ? 0 : 1,
        IDMCM,
        ENDECOMPL: complements,
      },
      toList: {
        IDGRUPOUSUARIO,
        FLUSERADM: state.group === "DESTINATÁRIO" ? 0 : 1,
        ENDERECO: [
          {
            DSMCM: state.set,
            IDSITE: state.site,
            IDMCM,
            ENDECOMPL: complements,
          },
        ],
      },
    };

    if (!editing) {
      onAddComplement(newComplement);
    } else {
      onUpdateComplement(newComplement);
    }
    clearState();
  };

  const handleDeleteComplement = (data: IAddressee.UserGroup) => {
    onDeleteComplement(Number(data.IDGRUPOUSUARIO));
  };

  const handleEditComplement = (data: IAddressee.UserGroup) => {
    setEditing(true);

    const group = data.ENDERECO[0];

    const dataToEdit: ComplementsState = {
      ...state,
      site: group.IDSITE ?? "",
      set: group.DSMCM,
      group: data.DSGRUPOUSUARIO ?? "",
    };

    dispatch({ type: "SETVALUES", payload: dataToEdit });
    setComplements(group.ENDECOMPL);
  };

  const handleSelectAllClick = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {};

  const isSelected = (complementId: string) =>
    selectedRows.indexOf(complementId) !== -1;
  const isValidForm =
    state?.site?.length > 0 &&
    state?.set?.length > 0 &&
    complements?.filter((complement) => complement.VLCOMPLEMENTO.length === 0)
      ?.length === 0;

  return loading ? (
    <></>
  ) : (
    <>
      <Paper style={{ marginTop: "18px", padding: "16px" }} elevation={0}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} lg={6}>
            <Autocomplete
              id="name"
              value={state.site}
              name="site"
              label="INSTALAÇÃO"
              onChange={handleChangeAutoComplete}
              choices={installationsSelectOptionsAutocomplete}
              disabled={editing}
            />
          </Grid>

          {state?.site && (
            <Grid item xs={12} sm={6} lg={3}>
              <Autocomplete
                id="set"
                value={state.set}
                name="set"
                label="SET"
                onChange={handleChangeAutoComplete}
                choices={setSelectOptionsAutocomplete}
                disabled={loadingSets || editing}
              />
            </Grid>
          )}

          {state?.set && (
            <Grid item xs={12} sm={6} lg={3}>
              <Autocomplete
                id="group"
                value={state.group}
                name="group"
                defaultValue="DESTINATÁRIO"
                label="GRUPO DO USUÁRIO"
                onChange={handleChangeAutoComplete}
                choices={userGroupsOptionsAutocomplete}
              />
            </Grid>
          )}

          {state?.site &&
            state?.set &&
            complements?.length > 0 &&
            complementsOfSet.map((complement, index) => {
              return (
                <Grid item xs={12} sm={6} md={3} key={index}>
                  <InputForm
                    textTransform="uppercase"
                    value={complements[index].VLCOMPLEMENTO}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      handleChangeComplements(e, index)
                    }
                    label={complement.DSCOMPLEMENTO}
                    id={`${complement.IDTIPOCOMPLEMENTO}`}
                    name={`comp-${complement.IDTIPOCOMPLEMENTO}`}
                    fullWidth
                  />
                </Grid>
              );
            })}

          <Grid
            item
            container
            xs={12}
            sm={6}
            lg={12}
            justifyContent="flex-end"
            alignItems="flex-end"
          >
            <Tooltip title="Cancelar">
              <ButtonCancelIcon
                color="secondary"
                style={{ width: "60px", marginRight: "16px" }}
                onClick={() => clearState()}
              >
                <Icons.Close
                  style={{ fontSize: "24px", color: "var(--orange)" }}
                />
              </ButtonCancelIcon>
            </Tooltip>

            <Tooltip title={!editing ? "Inserir" : "Atualizar"}>
              <Button
                color="primary"
                style={{ width: "60px" }}
                onClick={handleSubmitComplement}
                disabled={!isValidForm}
              >
                <Icons.Add style={{ fontSize: "24px" }} />
              </Button>
            </Tooltip>
          </Grid>
        </Grid>
      </Paper>

      <Paper className={classes.table} elevation={0}>
        {title && <TableTitle>{title}</TableTitle>}

        <TblContainer style={{ marginTop: "0" }}>
          <TblHead
            numSelected={selectedRows.length}
            rowCount={rows.length}
            onSelectAllClick={handleSelectAllClick}
          />
          <TableBody>
            {recordsAfterPagingAndSorting().length === 0 ? (
              <RowNoData />
            ) : (
              recordsAfterPagingAndSorting().map(
                (row: IAddressee.UserGroup, index: any) => {
                  const isItemSelected = isSelected(String(row.IDGRUPOUSUARIO));

                  return row.ENDERECO.map((address) => {
                    return (
                      <TableRow
                        hover
                        key={row.IDGRUPOUSUARIO}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        selected={isItemSelected}
                      >
                        <TableCell align="left">{address.IDSITE}</TableCell>
                        <TableCell align="left">{address.DSMCM}</TableCell>
                        <TableCell align="left">
                          {row.FLUSERADM === 1
                            ? "ADMINISTRADOR"
                            : "DESTINATÁRIO"}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={() => handleDeleteComplement(row)}
                          >
                            <Tooltip title="EXCLUIR">
                              <Icons.Delete fontSize="large" />
                            </Tooltip>
                          </IconButton>

                          <IconButton onClick={() => handleEditComplement(row)}>
                            <Tooltip title="EDITAR">
                              <Icons.Edit fontSize="large" />
                            </Tooltip>
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  });
                }
              )
            )}
          </TableBody>
        </TblContainer>

        <TblPagination />
      </Paper>
    </>
  );
}
export default memo(TableComplements);
